import { useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '新闻', to: '/category/news' },
  { label: '生活', to: '/category/life' },
  { label: '娱乐', to: '/category/entertainment' },
];

export default function SiteHeader() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setMobileOpen(false);
    setKeyword('');
  };

  const todayLabel = new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden md:block border-b border-background-200 bg-primary-950 text-primary-100">
        <div className="mx-auto flex h-9 max-w-[1440px] items-center justify-between px-4 md:px-6 text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-calendar-line"></i>
            {todayLabel}
          </span>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <i className="ri-customer-service-2-line text-accent-400"></i>
              客服热线 400-91-91-091
            </span>
            <Link to="/about" className="hover:text-background-50 transition-colors">
              关于我们
            </Link>
            <Link to="/contact" className="hover:text-background-50 transition-colors">
              联系我们
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="91网站首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-lg font-black text-background-50">
            91
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-bold text-foreground-950">91网站</span>
            <span className="mt-0.5 text-[11px] tracking-wide text-secondary-500">综合门户 · 资讯服务</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `relative flex items-center whitespace-nowrap rounded-md px-4 py-2 text-[15px] font-medium transition-colors ${
                  isActive
                    ? 'text-primary-600'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-[1px] left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-accent-500" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden lg:flex items-center">
            <div className="flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors">
              <i className="ri-search-line text-secondary-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索资讯 / 服务 / 娱乐"
                className="h-9 w-52 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
              />
              <button
                type="submit"
                className="flex h-7 items-center rounded-full bg-primary-500 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="搜索"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600 lg:hidden"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="菜单"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-primary-50 md:hidden"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              name="q"
              placeholder="输入关键词搜索"
              className="h-10 flex-1 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
            <button
              type="submit"
              className="h-10 shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <nav className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-background-200 pt-3">
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-700"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-700"
            >
              联系我们
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}